/**
 * Size Guide — custom element with accessible inches/cm toggle.
 *
 * Registers <fit-size-guide>. Each instance auto-discovers its toggle
 * button, source table, and converted table via data attributes. Converted
 * cells are pre-populated with source values server-side (no-JS fallback)
 * and overwritten with converted values on connection.
 *
 * Lifecycle: A fresh AbortController is created on every connectedCallback
 * so the element works correctly after disconnect/reconnect cycles in the
 * Shopify theme editor.
 */
(function () {
  'use strict';

  if (customElements.get('fit-size-guide')) return;

  class FitSizeGuide extends HTMLElement {
    #toggle;
    #sourceTbl;
    #convertedTbl;
    #ctrl;

    connectedCallback() {
      this.#ctrl = new AbortController();
      this.#toggle = this.querySelector('[data-fit-sg-toggle]');
      this.#sourceTbl =
        this.querySelector('[data-fit-sg-table="source"]') ||
        this.querySelector('[data-unit="inches"]');
      this.#convertedTbl =
        this.querySelector('[data-fit-sg-table="converted"]') ||
        this.querySelector('[data-unit="cm"]');

      if (!this.#toggle || !this.#sourceTbl || !this.#convertedTbl) return;

      this.#populateConvertedCells();
      this.#toggle.addEventListener('click', this.#onToggle, {
        signal: this.#ctrl.signal,
      });
      this.#applyState(this.#toggle.getAttribute('aria-checked') === 'true');
    }

    disconnectedCallback() {
      this.#ctrl?.abort();
      this.#ctrl = null;
    }

    /* ---- Toggle handler ---- */

    #onToggle = () => {
      const isCm = this.#toggle.getAttribute('aria-checked') !== 'true';
      this.#toggle.setAttribute('aria-checked', String(isCm));
      this.#applyState(isCm);
    };

    #applyState(showConverted) {
      this.#sourceTbl.classList.toggle('fit-sg__unit--active', !showConverted);
      this.#convertedTbl.classList.toggle('fit-sg__unit--active', showConverted);
    }

    /* ---- Unit conversion ---- */

    #populateConvertedCells() {
      const cells = this.#convertedTbl.querySelectorAll('[data-fit-sg-cell]');
      for (const cell of cells) {
        const raw = (cell.dataset.sourceValue || cell.dataset.inches || '').trim();
        const sourceUnit = FitSizeGuide.#normalizeUnit(
          cell.dataset.sourceUnit || this.dataset.sourceUnit || 'inches'
        );
        const targetUnit = FitSizeGuide.#normalizeUnit(
          cell.dataset.targetUnit || this.dataset.targetUnit || 'centimeters'
        );

        if (raw) cell.textContent = FitSizeGuide.#convertMeasurement(raw, sourceUnit, targetUnit);
      }
    }

    /**
     * Convert a measurement value between inches and centimetres.
     * Handles ranges ("32-34", "32–34", "32 — 34", "32 to 34", "32~34"), single
     * decimals ("26.5"), and integers. Non-numeric text passes through unchanged.
     */
    static #convertMeasurement(text, sourceUnit, targetUnit) {
      if (sourceUnit === targetUnit) return text;

      const source = FitSizeGuide.#normalizeUnit(sourceUnit);
      const target = FitSizeGuide.#normalizeUnit(targetUnit);
      const factor = source === 'centimeters' && target === 'inches' ? 1 / 2.54 : 2.54;
      const raw = text.trim();
      const numeric = '(-?\\d+(?:\\.\\d+)?)';
      const singleMatch = raw.match(new RegExp(`^\\s*${numeric}\\s*$`));
      const rangeMatch = raw.match(
        new RegExp(`^\\s*${numeric}\\s*(?:-|–|—|~|〜|～|to)\\s*${numeric}\\s*$`, 'i')
      );

      if (rangeMatch) {
        return [
          FitSizeGuide.#formatNumber(Number(rangeMatch[1]) * factor, target),
          FitSizeGuide.#formatNumber(Number(rangeMatch[2]) * factor, target),
        ].join('-');
      }

      if (singleMatch) {
        return FitSizeGuide.#formatNumber(Number(singleMatch[1]) * factor, target);
      }

      return text;
    }

    static #formatNumber(value, targetUnit) {
      if (targetUnit === 'centimeters') return String(Math.round(value));

      const rounded = Math.round(value * 10) / 10;
      return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
    }

    static #normalizeUnit(unit) {
      const normalized = String(unit || '').trim().toLowerCase();
      if (normalized === 'cm' || normalized.startsWith('centimeter')) return 'centimeters';
      return 'inches';
    }
  }

  customElements.define('fit-size-guide', FitSizeGuide);
})();
